import {
  coreExtensionData,
  createFrontendModule,
  SubPageBlueprint,
} from '@backstage/frontend-plugin-api';
import { Content } from '@backstage/core-components';
import catalogUnprocessedEntitiesPlugin from '@backstage/plugin-catalog-unprocessed-entities/alpha';
import devToolsPlugin from '@backstage/plugin-devtools/alpha';
import {
  devToolsAdministerPermission,
  devToolsExternalDependenciesReadPermission,
} from '@backstage/plugin-devtools-common';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { platformAdminViewPermission } from '@internal/plugin-rbac-common';

/**
 * Puts the whole DevTools page behind `devtools.administer`.
 *
 * Upstream only guards each tab, so without this the page shell and its tab
 * list render for everyone. `rbac.platformAdmins` pass through the permission
 * policy bypass; a tenant admin only gets in once their RBAC policy grants the
 * read-only DevTools actions, and the policy
 * (`packages/backend/src/modules/permissionPolicy.ts`) never lets RBAC grant
 * the Config tab or triggering a task.
 */
const gatedDevToolsPage = devToolsPlugin
  .getExtension('page:devtools')
  .override({
    factory(originalFactory) {
      const original = originalFactory();
      return [
        ...original,
        coreExtensionData.reactElement(
          <RequirePermission permission={devToolsAdministerPermission}>
            {original.get(coreExtensionData.reactElement)}
          </RequirePermission>,
        ),
      ];
    },
  });

/**
 * The new-frontend-system plugin ships Info, Config and Scheduled Tasks only;
 * External Dependencies is the reachability board for the endpoints listed
 * under `devTools.externalDependencies` in the environment's app-config.
 */
const externalDependenciesPage = SubPageBlueprint.make({
  name: 'external-dependencies',
  params: {
    path: 'external-dependencies',
    title: 'External Dependencies',
    loader: () =>
      import('@backstage/plugin-devtools').then(m => (
        <Content>
          <RequirePermission
            permission={devToolsExternalDependenciesReadPermission}
          >
            <m.ExternalDependenciesContent />
          </RequirePermission>
        </Content>
      )),
  },
});

export const devToolsModule = createFrontendModule({
  pluginId: 'devtools',
  extensions: [gatedDevToolsPage, externalDependenciesPage],
});

/**
 * The Unprocessed Entities tab lists every tenant's catalog entities that
 * failed processing, so it is for platform admins only - the same permission
 * its API enforces (`packages/backend/src/modules/unprocessedEntitiesModule.ts`).
 * A tenant admin granted read-only DevTools sees the tab refuse, not the data.
 */
const gatedUnprocessedEntitiesTab = catalogUnprocessedEntitiesPlugin
  .getExtension('sub-page:catalog-unprocessed-entities')
  .override({
    factory(originalFactory) {
      const original = originalFactory();
      return [
        ...original,
        coreExtensionData.reactElement(
          <RequirePermission permission={platformAdminViewPermission}>
            {original.get(coreExtensionData.reactElement)}
          </RequirePermission>,
        ),
      ];
    },
  });

export const unprocessedEntitiesModule = createFrontendModule({
  pluginId: 'catalog-unprocessed-entities',
  extensions: [gatedUnprocessedEntitiesTab],
});
